import React from 'react';
import { Users, Globe, Rocket, Heart, Shield, Award, Calendar, DollarSign, MessageSquare, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, Badge } from '../components/ui';
import { useTenant } from '../contexts/TenantContext';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { cn } from '../lib/utils';
import { SEO } from '../components/SEO';

export const AboutPage = () => {
  const { brand, colorMode, activeBrandId } = useTenant();
  const isDark = colorMode === 'dark';
  const brandName = brand.name;

  return (
    <div className="min-h-screen bg-white text-black dark:bg-black dark:text-white">
      <SEO 
        title={`About ${brandName || 'OrgSets'} | The Platform for Community Organizations`}
        description={`Learn more about ${brandName || 'OrgSets'}. We build powerful management tools that help multi-tier chapters, leagues, and associations stay perfectly organized.`}
      />
      <PublicHeader />

      <main className="pb-20">
          <section className="grid grid-cols-1 lg:grid-cols-2 border-b-[8px] border-black dark:border-white min-h-[50vh] bg-black text-white dark:bg-white dark:text-black mb-24">
            <div className="p-8 pt-28 md:p-16 md:pt-28 lg:p-12 lg:pt-28 xl:p-24 xl:pt-28 flex flex-col justify-center border-b-[8px] lg:border-b-0 lg:border-r-[8px] border-black dark:border-white">
              <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-6xl xl:text-7xl 2xl:text-[100px] font-black uppercase tracking-tighter leading-[0.85] mb-8 break-words">
                Built For <br/> Leaders. Not <br/> Algorithms.
              </h1>
              <p className="text-xl md:text-2xl font-bold leading-snug mb-12">
                An effortless management engine tailored for community organizations. Cut the noise and get back to leading.
              </p>
            </div>
            <div className="p-8 md:p-16 flex flex-col justify-center items-center h-full relative overflow-hidden bg-brand-primary text-white">
              <div className="absolute top-0 right-0 w-64 h-64 bg-pink-500 rounded-bl-full border-b-[8px] border-l-[8px] border-black dark:border-white" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-secondary rounded-tr-full border-t-[8px] border-r-[8px] border-black dark:border-white" />
              <div className="text-5xl md:text-7xl font-black uppercase tracking-widest text-center relative z-10 leading-[0.9]">
                Made For <br/> <span className="text-brand-secondary">People</span>
              </div>
            </div>
          </section>

        <div className="max-w-6xl mx-auto px-4 mt-8">

          {/* Our Story & Origin */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-20 items-center">
            <div className="space-y-6">
              <h2 className={cn("text-3xl font-bold tracking-tight", isDark ? "text-white" : "text-navy-900 dark:text-white")}>Our Story</h2>
              <div className="h-1.5 w-16 bg-brand-primary mb-8" />
              <p className="text-lg font-black uppercase tracking-widest opacity-80 leading-relaxed">
                BUILT FOR LEADERS. NO ADS. NO ALGORITHMS. JUST TOOLS THAT WORK.
              </p>
              <ul className="space-y-4 mt-6">
                <li className="flex items-start gap-3">
                  <div className="mt-1"><Sparkles size={18} className="text-brand-primary" /></div>
                  <span className="opacity-70 leading-relaxed text-base"><strong>The Problem:</strong> Managing an organization is too painful. Leaders are stuck between chaotic group chats and expensive corporate tools.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="mt-1"><Sparkles size={18} className="text-brand-primary" /></div>
                  <span className="opacity-70 leading-relaxed text-base"><strong>The Solution:</strong> An effortless management engine tailored specifically for community organizations.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="mt-1"><Sparkles size={18} className="text-brand-primary" /></div>
                  <span className="opacity-70 leading-relaxed text-base"><strong>The Result:</strong> By providing clear structure, an easy-to-use directory, and <Link to="/features#ledger" className="underline hover:text-brand-primary transition-colors">powerful automation</Link>, we eliminate administrative friction and let leaders get back to leading.</span>
                </li>
              </ul>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-xl aspect-square bg-gray-100 min-h-[300px] dark:bg-slate-800">
               <img 
                src="/images/about_story.png" 
                alt="Community volunteers working together"
                className="w-full h-full object-cover block"
                referrerPolicy="no-referrer"
              />
            </div>
          </div>

          {/* Who We Serve */}
          <section className="mb-20">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold tracking-tight mb-4">Who We Serve</h2>
              <p className="text-lg opacity-60 max-w-2xl mx-auto">
                From local hobbies to structured national organizations, {brandName} adapts to the way your community operates.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className={cn("p-8 rounded-3xl border transition-all duration-300 hover:shadow-md", isDark ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-100")}>
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-brand-primary/10 rounded-2xl text-brand-primary">
                    <Award size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Scout Troops & Youth Groups</h3>
                    <p className="opacity-75 text-sm leading-relaxed">
                      Track badge progress, record advancements, manage outdoor event RSVPs, assign leadership roles, and communicate securely with parents in a fully structured, child-safety-conscious environment.
                    </p>
                  </div>
                </div>
              </div>

              <div className={cn("p-8 rounded-3xl border transition-all duration-300 hover:shadow-md", isDark ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-100")}>
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-brand-primary/10 rounded-2xl text-brand-primary">
                    <Calendar size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Sports Teams & Leagues</h3>
                    <p className="opacity-75 text-sm leading-relaxed">
                      Coordinate practice schedules, game locations, and tournament rosters. Send automated event reminders, track player availability, and collect uniform fees all in one place.
                    </p>
                  </div>
                </div>
              </div>

              <div className={cn("p-8 rounded-3xl border transition-all duration-300 hover:shadow-md", isDark ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-100")}>
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-brand-primary/10 rounded-2xl text-brand-primary">
                    <Users size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Non-Profits & Community Projects</h3>
                    <p className="opacity-75 text-sm leading-relaxed">
                      Manage volunteer sign-ups, log service hours, host project coordination discussions, share safety protocols in the document vault, and keep your board aligned with transparent minutes.
                    </p>
                  </div>
                </div>
              </div>

              <div className={cn("p-8 rounded-3xl border transition-all duration-300 hover:shadow-md", isDark ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-100")}>
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-brand-primary/10 rounded-2xl text-brand-primary">
                    <MessageSquare size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">HOAs, Committees & Clubs</h3>
                    <p className="opacity-75 text-sm leading-relaxed">
                      Publish announcements, send monthly newsletters, organize neighborhood clean-ups, share budget sheets, and hold transparent elections for board members or committee leads.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="mb-20">
            <div className="bg-navy-900 rounded-3xl p-12 text-white relative overflow-hidden shadow-2xl">
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-white/10">
                <div className="py-6 md:py-0">
                  <p className="text-5xl font-black text-brand-primary font-mono mb-2">100%</p>
                  <p className="text-blue-100/70 uppercase tracking-widest text-xs font-bold font-mono">User Data Ownership</p>
                </div>
                <div className="py-6 md:py-0">
                  <p className="text-5xl font-black text-teal-400 font-mono mb-2">99.9%</p>
                  <p className="text-blue-100/70 uppercase tracking-widest text-xs font-bold font-mono">Platform Uptime</p>
                </div>
                <div className="py-6 md:py-0">
                  <p className="text-5xl font-black text-white font-mono mb-2">0</p>
                  <p className="text-blue-100/70 uppercase tracking-widest text-xs font-bold font-mono">Tracking Ads or Sold Data</p>
                </div>
              </div>
              <div className="absolute -right-20 -top-20 w-80 h-80 bg-brand-primary/20 rounded-full blur-[120px]" />
            </div>
          </section>

          {/* Philosophy / Values detail */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-20 items-center">
            <div className="rounded-3xl overflow-hidden shadow-xl aspect-square bg-gray-100 min-h-[300px] md:order-1 order-2 dark:bg-slate-800">
               <img 
                src="/images/about_philosophy.png" 
                alt="Active family and community meal gathering outdoors"
                className="w-full h-full object-cover block"
                referrerPolicy="no-referrer"
              />
            </div>
            <div className="space-y-6 md:order-2 order-1">
              <h2 className="text-3xl font-bold text-navy-900 dark:text-white tracking-tight">Engineered for League & Chapter Governance.</h2>
              <div className="h-1.5 w-16 bg-teal-500 mb-8" />
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg font-bold">
                Software should serve people, not harvest attention.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="mt-1"><Shield size={18} className="text-teal-500" /></div>
                  <span className="text-gray-600 dark:text-gray-400 leading-relaxed text-base">Our features act as a professional command center so you can govern your group with absolute clarity.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="mt-1"><Shield size={18} className="text-teal-500" /></div>
                  <span className="text-gray-600 dark:text-gray-400 leading-relaxed text-base">From <Link to="/features#vault" className="underline hover:text-brand-primary transition-colors">secure vaults for medical forms</Link> to <Link to="/pricing" className="underline hover:text-brand-primary transition-colors">automated financial tracking</Link>, everything is designed to eliminate drag.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="mt-1"><Shield size={18} className="text-teal-500" /></div>
                  <span className="text-gray-600 dark:text-gray-400 leading-relaxed text-base">We keep the layout fast, lightweight, and intuitive so members of all technological comfort levels can operate efficiently.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Core Values Section */}
          <section className="mb-20">
            <div className="bg-navy-900 rounded-[2rem] p-8 md:p-20 text-white relative overflow-hidden shadow-2xl">
              <div className="relative z-10 max-w-5xl">
                <h2 className="text-3xl md:text-5xl font-black mb-12 font-display leading-tight italic !text-white">The values that guide {brandName}.</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
                  <div className="space-y-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border border-white/10 dark:bg-slate-900">
                      <Globe size={24} className="text-white" />
                    </div>
                    <h4 className="font-bold text-xl text-white">Growing Together</h4>
                    <p className="text-blue-100/70 text-sm leading-relaxed">
                      Simple tools designed for local clubs that can scale gracefully into large multi-tiered chapters and regional networks.
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center border border-white/10">
                      <Rocket size={24} className="text-teal-400" />
                    </div>
                    <h4 className="font-bold text-xl text-white">Less Admin, More Action</h4>
                    <p className="text-blue-100/70 text-sm leading-relaxed">
                      We automate calendars, communications, and tracking spreadsheets so you can spend less time doing admin work and more time with your group.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center border border-white/10">
                      <Shield size={24} className="text-purple-400" />
                    </div>
                    <h4 className="font-bold text-xl text-white">Privacy is Paramount</h4>
                    <p className="text-blue-100/70 text-sm leading-relaxed">
                      No advertisements, no sellable activity logs, and strict access controls to ensure your community's data stays exclusively yours.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center border border-white/10">
                      <Sparkles size={24} className="text-amber-400" />
                    </div>
                    <h4 className="font-bold text-xl text-white">Radical Simplicity</h4>
                    <p className="text-blue-100/70 text-sm leading-relaxed">
                      No complex interfaces or enterprise vocabulary. Clean, human terms and straightforward flows for all generations.
                    </p>
                  </div>
                </div>
              </div>
              <div className="absolute -right-20 -top-20 w-80 h-80 bg-brand-primary/20 rounded-full blur-[120px]" />
            </div>
          </section>



          {/* Contact Details Grid */}
          <div className="border-t border-gray-200 dark:border-gray-800 pt-16 mb-16 grid grid-cols-1 md:grid-cols-3 gap-12">
             <div className="text-center md:text-left">
                <h3 className="font-bold text-navy-900 dark:text-white mb-4 font-mono text-xs uppercase tracking-[0.2em] opacity-80">General Inquiries</h3>
                <p className="text-gray-500 text-sm leading-loose">contact@{(brandName || '').toLowerCase().replace(' ', '')}.com</p>
             </div>
             <div className="text-center md:text-left">
                <h3 className="font-bold text-navy-900 dark:text-white mb-4 font-mono text-xs uppercase tracking-[0.2em] opacity-80">Media & Press</h3>
                <p className="text-gray-500 text-sm leading-loose">press@{(brandName || '').toLowerCase().replace(' ', '')}.com</p>
             </div>
             <div className="text-center md:text-left">
                <h3 className="font-bold text-navy-900 dark:text-white mb-4 font-mono text-xs uppercase tracking-[0.2em] opacity-80">Partnerships</h3>
                <p className="text-gray-500 text-sm leading-loose">partners@{(brandName || '').toLowerCase().replace(' ', '')}.com</p>
             </div>
          </div>

          {/* Bottom CTA */}
          <section className="p-12 text-center mb-16 bg-white text-black dark:bg-black dark:text-white border-[8px] border-black dark:border-white shadow-[16px_16px_0px_#000] dark:shadow-[16px_16px_0px_#fff] rounded-none">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-black mb-6 tracking-tighter">
                Spend less time on admin, and more time with your community.
              </h2>
              <p className="text-lg md:text-xl mb-10 font-bold">
                Start your organization for free today. Setup in 60 seconds. No credit card required.
              </p>
              <div className="flex justify-center">
                 <Link to="/sign-up" className="px-10 py-5 text-lg font-black inline-flex bg-black text-white dark:bg-white dark:text-black border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff] rounded-none hover:translate-x-[-4px] hover:translate-y-[-4px] hover:shadow-[12px_12px_0px_#000] dark:hover:shadow-[12px_12px_0px_#fff] uppercase tracking-widest transition-all">
                    CREATE YOUR ORG
                 </Link>
              </div>
            </div>
          </section>

        </div>
      </main>
      <PublicFooter />
    </div>
  );
};

